import { useEffect, useMemo, useState } from "react";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import api from "@/lib/api";

export default function BullMapSection() {
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    api.get("/bull-locations").then((r) => setLocations(r.data)).catch(() => {});
  }, []);

  // merge same town across years so dots don't stack
  const markers = useMemo(() => {
    const m = new Map();
    for (const l of locations) {
      const k = `${l.city}|${l.state}`;
      if (m.has(k)) m.get(k).count += l.count || 1;
      else m.set(k, { ...l, count: l.count || 1 });
    }
    return [...m.values()];
  }, [locations]);

  const stats = useMemo(() => {
    const bulls = locations.reduce((s, l) => s + (l.count || 1), 0);
    const states = new Set(locations.filter((l) => l.state !== "AB").map((l) => l.state)).size;
    const canada = locations.some((l) => l.state === "AB");
    return { bulls, states, towns: markers.length, canada };
  }, [locations, markers]);

  if (locations.length === 0) return null;

  return (
    <section data-testid="bull-map-section" className="section-deep relative py-24 md:py-32 overflow-hidden">
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-10 items-end">
          <div className="md:col-span-7">
            <div className="eyebrow mb-4" style={{ color: "var(--sky-pale)" }}>
              2022–2026 Deliveries
            </div>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.95]" style={{ color: "var(--surface)" }}>
              Working for producers,<br />coast to coast.
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="text-base md:text-lg" style={{ color: "rgba(255,255,255,0.8)" }}>
              These bulls sell here and go to work everywhere. Every dot is a ranch running
              Bulls of the Big Sky genetics — {stats.bulls} bulls delivered to {stats.towns} towns
              across {stats.states} states{stats.canada ? " and Canada" : ""} in the last five sales alone*.
            </p>
            <div className="mt-6 flex gap-10">
              <Stat n={stats.bulls} label="Bulls" />
              <Stat n={stats.states} label="States" />
              <Stat n={stats.towns} label="Towns" />
            </div>
          </div>
        </div>

        <div className="border" style={{ borderColor: "rgba(191,212,224,0.25)" }}>
          <ComposableMap projection="geoAlbersUsa" style={{ width: "100%", height: "auto" }} data-testid="bull-map">
            <Geographies geography="/us-states.json">
              {({ geographies }) =>
                geographies.map((geo) => (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill="rgba(191,212,224,0.08)"
                    stroke="rgba(191,212,224,0.3)"
                    strokeWidth={0.6}
                    style={{ default: { outline: "none" }, hover: { outline: "none", fill: "rgba(191,212,224,0.14)" }, pressed: { outline: "none" } }}
                  />
                ))
              }
            </Geographies>
            {markers.map((l) => (
              <Marker key={`${l.city}|${l.state}`} coordinates={[l.lng, l.lat]}>
                <circle
                  r={2 + Math.sqrt(l.count || 1) * 1.6}
                  fill="var(--hay)"
                  fillOpacity={0.7}
                  stroke="#E8CE96"
                  strokeWidth={0.8}
                >
                  <title>{`${l.city}, ${l.state} — ${l.count} bull${l.count > 1 ? "s" : ""}`}</title>
                </circle>
              </Marker>
            ))}
          </ComposableMap>
        </div>
        <div className="mt-4 text-xs eyebrow-muted" style={{ color: "rgba(255,255,255,0.5)" }}>
          *Map shows the last 5 sale years (2022–2026) of 17 total sales  •  Dot size = number of bulls delivered  •  Hover a dot for the town
          {stats.canada ? "  •  Plus buyers in Alberta, Canada (off map)" : ""}
        </div>
      </div>
    </section>
  );
}

function Stat({ n, label }) {
  return (
    <div>
      <div className="font-display text-4xl md:text-5xl" style={{ color: "var(--hay)" }}>{n}</div>
      <div className="eyebrow text-[10px] mt-1" style={{ color: "rgba(255,255,255,0.6)" }}>{label}</div>
    </div>
  );
}
